import { create } from 'zustand';
import type { Item, ItemType, BoundingBox } from './canvasStore';
import type { BoardProfile } from '@/lib/boardProfileService';
import type { SpatialCluster } from '@/lib/spatialClustering';

export interface ItemState {
  items: Item[];
  allItems: Item[];
  isLoading: boolean;
  isSaving: boolean;
  isEditingItem: boolean;
  activeEditor: import('@tiptap/react').Editor | null;
  wallScrollOffset: number;
  itemMinHeights: Record<string, number>;
  copiedItems: Item[];

  // Board semantic profile
  boardProfile: BoardProfile | null;
  isProfilingBoard: boolean;

  // Spatial clustering
  spatialClusters: SpatialCluster[];
  isClusteringEnabled: boolean;
  isDetectingClusters: boolean;
  highlightedClusterId: string | null;
  lastClusteredItemIds: string[];

  // Actions
  setItems: (items: Item[]) => void;
  setAllItems: (items: Item[]) => void;
  setIsLoading: (loading: boolean) => void;
  setIsSaving: (saving: boolean) => void;
  setIsEditingItem: (editing: boolean) => void;
  setActiveEditor: (editor: import('@tiptap/react').Editor | null) => void;
  setWallScrollOffset: (offset: number) => void;
  setItemMinHeight: (itemId: string, minHeight: number) => void;
  setCopiedItems: (items: Item[]) => void;
  setBoardProfile: (profile: BoardProfile | null) => void;
  setIsProfilingBoard: (profiling: boolean) => void;
  setSpatialClusters: (clusters: SpatialCluster[]) => void;
  setClusteringEnabled: (enabled: boolean) => void;
  setIsDetectingClusters: (detecting: boolean) => void;
  setHighlightedClusterId: (id: string | null) => void;
  setLastClusteredItemIds: (ids: string[]) => void;
  updateItemInState: (id: string, updates: Partial<Item>) => void;
  removeItemFromState: (id: string) => void;
  addItemToState: (item: Item) => void;
  getBoundingBox: () => BoundingBox | null;
}

export const useItemStore = create<ItemState>((set, get) => ({
  items: [],
  allItems: [],
  isLoading: true,
  isSaving: false,
  isEditingItem: false,
  activeEditor: null,
  wallScrollOffset: 0,
  itemMinHeights: {},
  copiedItems: [],
  boardProfile: null,
  isProfilingBoard: false,
  spatialClusters: [],
  isClusteringEnabled: false,
  isDetectingClusters: false,
  highlightedClusterId: null,
  lastClusteredItemIds: [],

  setItems: (items) => set({ items }),
  setAllItems: (items) => set({ allItems: items }),
  setIsLoading: (loading) => set({ isLoading: loading }),
  setIsSaving: (saving) => set({ isSaving: saving }),
  setIsEditingItem: (editing) => set({ isEditingItem: editing }),
  setActiveEditor: (editor) => set({ activeEditor: editor }),
  setWallScrollOffset: (offset) => set({ wallScrollOffset: offset }),

  setItemMinHeight: (itemId, minHeight) =>
    set((state) => {
      const current = state.itemMinHeights[itemId] ?? 0;
      if (Math.abs(current - minHeight) < 6) return {};
      return {
        itemMinHeights: {
          ...state.itemMinHeights,
          [itemId]: minHeight,
        },
      };
    }),

  setCopiedItems: (items) => set({ copiedItems: items }),
  setBoardProfile: (profile) => set({ boardProfile: profile }),
  setIsProfilingBoard: (profiling) => set({ isProfilingBoard: profiling }),
  setSpatialClusters: (clusters) => set({ spatialClusters: clusters }),
  setClusteringEnabled: (enabled) => set({ isClusteringEnabled: enabled }),
  setIsDetectingClusters: (detecting) => set({ isDetectingClusters: detecting }),
  setHighlightedClusterId: (id) => set({ highlightedClusterId: id }),
  setLastClusteredItemIds: (ids) => set({ lastClusteredItemIds: ids }),

  updateItemInState: (id, updates) => set((state) => ({
    items: state.items.map(item => item.id === id ? { ...item, ...updates } : item),
    allItems: state.allItems.map(item => item.id === id ? { ...item, ...updates } : item),
  })),

  removeItemFromState: (id) => set((state) => ({
    items: state.items.filter(item => item.id !== id),
    allItems: state.allItems.filter(item => item.id !== id),
  })),

  addItemToState: (item) => set((state) => ({
    items: [...state.items, item],
    allItems: [item, ...state.allItems],
  })),

  getBoundingBox: () => {
    const state = get();
    if (state.items.length === 0) return null;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const item of state.items) {
      minX = Math.min(minX, item.position_x);
      minY = Math.min(minY, item.position_y);
      maxX = Math.max(maxX, item.position_x + item.width);
      maxY = Math.max(maxY, item.position_y + item.height);
    }

    return {
      minX, minY, maxX, maxY,
      centerX: (minX + maxX) / 2,
      centerY: (minY + maxY) / 2,
      width: maxX - minX,
      height: maxY - minY,
    };
  },
}));
